import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapLocationData, MarkerConfig, MarkerCardConfig, MapMode } from '@hero/web-components';
import type { GoogleMapWithMarkerProps } from '@hero/web-components';
import { useMapMarkerColors } from './useMapMarkerColors';

export interface UseMapMarkersOptions {
  /** Current locations */
  locations: MapLocationData[];
  /** Current editing index */
  editingIndex: number | null;
  /** Current map mode */
  mode: MapMode;
  /** Custom marker config function */
  getMarkerConfig?: GoogleMapWithMarkerProps['getMarkerConfig'];
  /** Marker drag end handler */
  onMarkerDragEnd: (
    event: google.maps.MapMouseEvent,
    index: number,
    currentLocations: MapLocationData[],
  ) => Promise<void>;
  /** Whether Google Maps API is loaded */
  isLoaded: boolean;
}

/**
 * Hook for generating marker configurations from locations
 * Converts locations array to MarkerConfig array with icons, labels, and cards
 */
export function useMapMarkers({
  locations,
  editingIndex,
  mode,
  getMarkerConfig,
  onMarkerDragEnd,
  isLoaded,
}: UseMapMarkersOptions): MarkerConfig[] {
  const { t } = useTranslation();
  const { markerColors } = useMapMarkerColors();

  const markers: MarkerConfig[] = useMemo(() => {
    if (!isLoaded || typeof google === 'undefined' || !google.maps) {
      return [];
    }

    return locations.map((location, index) => {
      const isOrigin = index === 0;
      const isBeingEdited = editingIndex === index;

      // Default marker icon
      const defaultIcon: google.maps.Symbol = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: isBeingEdited
          ? markerColors.pending.fillColor
          : isOrigin
          ? markerColors.pickup.fillColor
          : markerColors.dropoff.fillColor,
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
      };

      // Red border for inaccurate locations (unless editing)
      if (location.isAccurateLocation === false && !isBeingEdited) {
        defaultIcon.strokeColor = markerColors.notAccurateLocation.fillColor;
        defaultIcon.strokeWeight = 3;
      }

      // Default marker label
      const defaultLabel: google.maps.MarkerLabel = {
        text: isOrigin ? 'P' : String(index),
        color: '#FFFFFF',
        fontSize: '12px',
        fontWeight: 'bold',
      };

      // Default card config
      const defaultCard: MarkerCardConfig = {
        title: isOrigin
          ? t('location.pickup', 'Pickup')
          : t('location.dropoff', `Dropoff ${index}`),
      };

      // Use custom marker config function if provided
      let markerIcon = defaultIcon;
      let markerLabel = defaultLabel;
      let cardConfig = defaultCard;

      if (getMarkerConfig) {
        const customConfig = getMarkerConfig(location, index);
        if (customConfig.icon) markerIcon = customConfig.icon as google.maps.Symbol;
        if (customConfig.label) {
          markerLabel =
            typeof customConfig.label === 'string'
              ? { text: customConfig.label, color: '#FFFFFF', fontSize: '12px', fontWeight: 'bold' }
              : customConfig.label;
        }
        if (customConfig.card) cardConfig = customConfig.card;
      }

      return {
        location,
        card: cardConfig,
        icon: markerIcon,
        label: markerLabel,
        draggable: mode === 'add_new_location' || (mode === 'edit' && editingIndex === index),
        onDragEnd: position => {
          onMarkerDragEnd(
            {
              latLng: () =>
                isLoaded && typeof google !== 'undefined' && google.maps
                  ? new google.maps.LatLng(position.lat, position.lng)
                  : null,
            } as any,
            index,
            locations,
          );
        },
      };
    });
  }, [locations, editingIndex, mode, getMarkerConfig, t, isLoaded, onMarkerDragEnd, markerColors]);

  return markers;
}
